// Per-trial World — agents, calendars, prerolled dice.
//
// Everything in here is derived deterministically from a single seed plus
// the process + intake + global params. Same seed → same world.

import { Calendar, OooBlock, PRNG, TZ_OFFSET_FROM_ET, TzAbbr, makePRNG } from "./sim-calendar";
import type { Intake, ProcessDef, ProcessParty } from "./processes";

// Simulation window: 90 days * 24 hours. Deep processes (13+ steps with
// multi-day OOO blocks on the naive path) can otherwise time out before the
// last step lands, which silently inflates the failure rate and hides the
// human-routed cost numbers.
export const SIM_WINDOW_HRS = 90 * 24;

type SizeTier = "startup" | "smb" | "mid" | "enterprise";

interface RosterShape {
  internal: number;
  customer: number;
  external: number;
}

// Roster sizes by org-size tier and party "type". "type" is a soft category
// inferred from the party's role label — see classifyParty().
const ROSTER_BY_SIZE: Record<SizeTier, RosterShape> = {
  startup:    { internal: 1, customer: 1, external: 1 },
  smb:        { internal: 2, customer: 1, external: 1 },
  mid:        { internal: 4, customer: 2, external: 2 },
  enterprise: { internal: 8, customer: 3, external: 2 },
};

export type PartyType =
  | "legal" | "procurement" | "finance" | "hr" | "security"
  | "engineering" | "sales" | "government" | "external" | "default";

interface PartyPrior {
  dropRate: [number, number];
  errorRate: [number, number];
  latencyMu: [number, number];
}

// Per-function priors. "function" here is a coarse bucket inferred from the
// party label — e.g. "Legal", "Procurement", "HR", "Finance", or a generic
// fallback. Each prior is (min, max) for the random draw.
const PARTY_PRIORS: Record<PartyType, PartyPrior> = {
  legal:        { dropRate: [0.003, 0.012], errorRate: [0.01, 0.03], latencyMu: [18, 48] },
  procurement:  { dropRate: [0.010, 0.030], errorRate: [0.03, 0.06], latencyMu: [12, 36] },
  finance:      { dropRate: [0.006, 0.020], errorRate: [0.02, 0.05], latencyMu: [10, 30] },
  hr:           { dropRate: [0.008, 0.025], errorRate: [0.02, 0.04], latencyMu: [6, 22] },
  security:     { dropRate: [0.005, 0.015], errorRate: [0.02, 0.04], latencyMu: [14, 36] },
  engineering:  { dropRate: [0.005, 0.020], errorRate: [0.03, 0.06], latencyMu: [6, 18] },
  sales:        { dropRate: [0.008, 0.030], errorRate: [0.02, 0.05], latencyMu: [4, 14] },
  government:   { dropRate: [0.010, 0.030], errorRate: [0.04, 0.08], latencyMu: [48, 120] },
  external:     { dropRate: [0.005, 0.020], errorRate: [0.03, 0.06], latencyMu: [24, 72] },
  default:      { dropRate: [0.008, 0.020], errorRate: [0.02, 0.05], latencyMu: [8, 24] },
};

// First-name pools (deterministic via PRNG, just need something readable).
const FIRST_NAMES = [
  "Bob", "Eva", "Carl", "Maya", "Jin", "Ali", "Sara", "Theo",
  "Lin", "Omar", "Anya", "Diego", "Priya", "Noor", "Kai", "Rosa",
];

// Continental + Hawaii/Alaska TZs. ET is the anchor; the others appear in
// the pool when overlap < 1.
const TIMEZONES: TzAbbr[] = ["HST", "AKST", "PT", "MT", "CT", "ET"];

export interface Agent {
  id: string;
  idNum: number;
  name: string;
  partyId: string;
  partyName: string;
  orgId: string;
  type: PartyType;
  timezone: TzAbbr;
  tzOffset: number;
  isPrimary: boolean;
  dropRate: number;
  errorRate: number;
  latencyMu: number;
  latencySigma: number;
  loadFactor: number;
  oooBlocks: OooBlock[];
  calendar: Calendar;
}

export interface World {
  proc: ProcessDef;
  intake: Intake | Partial<Intake> | null | undefined;
  // `params` is consumed by the engine — we keep it loosely typed here so the
  // engine's defensive defaults (`params?.foo ?? default`) keep working when
  // callers pass partial knob state.
  params: Record<string, number> | null | undefined;
  seed: number;
  windowHrs: number;
  agentsByParty: Map<string, Agent[]>;
  allAgents: Agent[];
}

interface AgentOpts {
  id: string;
  partyId: string;
  partyName: string;
  orgId: string;
  type: PartyType;
  prior: PartyPrior;
  isPrimary: boolean;
  sizeTier: SizeTier;
  params: Record<string, number> | null | undefined;
}

// --------------------------------------------------------------------------
// Public entry point.
// --------------------------------------------------------------------------

/**
 * Generate a complete World for one trial.
 */
export function generateWorld(
  proc: ProcessDef,
  intake: Intake | Partial<Intake> | null | undefined,
  params: Record<string, number> | null | undefined,
  seed: number,
): World {
  const rand = makePRNG(seed);
  const sizeTier = (intake?.size as SizeTier | undefined) || "smb";
  const roster = ROSTER_BY_SIZE[sizeTier] || ROSTER_BY_SIZE.smb;

  const agentsByParty = new Map<string, Agent[]>();
  const allAgents: Agent[] = [];
  let agentId = 0;

  for (const party of proc.parties) {
    const type = classifyParty(party, proc);                 // "legal" | "external" | …
    const headcount = headcountFor(party, type, roster);     // 1..8
    const prior = PARTY_PRIORS[type] || PARTY_PRIORS.default;
    const team: Agent[] = [];
    for (let i = 0; i < headcount; i++) {
      const agent = makeAgent(rand, {
        id: `a${agentId++}`,
        partyId: party.id,
        partyName: party.name,
        orgId: party.org,
        type,
        prior,
        isPrimary: i === 0,
        sizeTier,
        params,
      });
      team.push(agent);
      allAgents.push(agent);
    }
    agentsByParty.set(party.id, team);
  }

  // Trial begins at hour 0 = Monday 9am ET. The originator (first step's
  // `from`) hands off at hour 0. Dice for each (agent, stepIdx) are derived
  // on demand by sim-engine.js from a hash of (seed, agentIdNum, stepIdx)
  // — eliminates the per-trial Map<string, object> that was the dominant
  // garbage source.
  return {
    proc,
    intake,
    params,
    seed,
    windowHrs: SIM_WINDOW_HRS,
    agentsByParty,
    allAgents,
  };
}

// --------------------------------------------------------------------------
// Internals.
// --------------------------------------------------------------------------

function makeAgent(rand: PRNG, opts: AgentOpts): Agent {
  const { id, partyId, partyName, orgId, type, prior, isPrimary, params } = opts;

  // Knob multipliers — each user-facing dial maps onto a multiplicative
  // factor against the function's prior. Anchored on each knob's default so
  // dragging the slider produces a proportional effect.
  const dropMul   = clamp01(params?.dropRate  ?? 0.015) / 0.015;
  const errorMul  = clamp01(params?.errorRate ?? 0.04)  / 0.04;
  const ooorMul   = clamp01(params?.oooRate   ?? 0.05)  / 0.05;
  // "Average response time" — global queue-wait scaler. Default 24h → 1.0×.
  // Higher value stretches everyone's typical response; lower compresses it.
  const responseMul = Math.max(0.1, (params?.avgResponseHrs ?? 24) / 24);

  // "Working-hours overlap" — controls two compounding effects:
  //   (1) probability that this agent works in the anchor TZ (ET) vs spread
  //       across NA (HST..ET) — so cross-TZ handoffs hit the time-of-day
  //       wall more often when overlap is low;
  //   (2) a queue-wait inflation factor: when overlap is low, each agent's
  //       typical response also gets stretched because handoffs that fall
  //       outside their counterpart's hours sit longer than the calendar
  //       gap alone would suggest. 1.0× at overlap=1, 1.6× at overlap=0.
  const overlap = clamp01(params?.overlap ?? 0.7);
  const overlapPenalty = 1 + (1 - overlap) * 0.6;
  const tz: TzAbbr = rand() < overlap ? "ET" : TIMEZONES[Math.floor(rand() * TIMEZONES.length)];

  const dropRate = lerp(prior.dropRate, rand()) * dropMul;
  const errorRate = lerp(prior.errorRate, rand()) * errorMul;
  const latencyMu = lerp(prior.latencyMu, rand()) * responseMul * overlapPenalty;
  const latencySigma = 0.55 + rand() * 0.3;
  const loadFactor = 0.85 + rand() * 0.7; // 0.85x to 1.55x — busier people are slower

  // OOO blocks. Probability-of-block scaled by user's oooRate knob.
  // 0-2 vacation blocks of 3-7 days; sick days as 1-day blocks at ~3%/day,
  // multiplied by ooorMul.
  const oooBlocks = generateOOO(rand, SIM_WINDOW_HRS, ooorMul);

  const tzOffset = TZ_OFFSET_FROM_ET[tz];
  const calendar = new Calendar({
    windowHrs: SIM_WINDOW_HRS,
    tzOffsetHrs: tzOffset,
    oooBlocks,
  });

  // Numeric agent index — used by the engine to derive per-(agent, step)
  // dice on demand without a Map allocation.
  const idNum = parseInt(id.slice(1), 10) || 0;

  return {
    id,
    idNum,
    name: FIRST_NAMES[Math.abs(hashString(id)) % FIRST_NAMES.length],
    partyId,
    partyName,
    orgId,
    type,
    timezone: tz,
    tzOffset,
    isPrimary,
    dropRate,
    errorRate,
    latencyMu,
    latencySigma,
    loadFactor,
    oooBlocks,
    calendar,
  };
}

function generateOOO(rand: PRNG, windowHrs: number, mul: number): OooBlock[] {
  const blocks: OooBlock[] = [];
  // Vacation: each person, 0–2 blocks.
  const vacationCount = rand() < 0.6 * mul ? (rand() < 0.4 ? 2 : 1) : 0;
  for (let i = 0; i < vacationCount; i++) {
    const startDay = Math.floor(rand() * (windowHrs / 24 - 7));
    const lengthDays = 3 + Math.floor(rand() * 5); // 3–7 days
    blocks.push([startDay * 24, (startDay + lengthDays) * 24]);
  }
  // Sick days: per-workday roll, ~3% baseline scaled by mul.
  const pSick = 0.03 * mul;
  for (let day = 0; day < windowHrs / 24; day++) {
    const dayOfWeek = day % 7;
    if (dayOfWeek >= 5) continue;
    if (rand() < pSick) blocks.push([day * 24, (day + 1) * 24]);
  }
  return blocks.sort((a, b) => a[0] - b[0]);
}

// Heuristic classifier — maps a party's label into a coarse function bucket
// that drives its priors. Strings come from human-authored process catalog,
// so we err on the side of substring matching.
function classifyParty(party: ProcessParty, proc: ProcessDef): PartyType {
  const label = (party.name || "").toLowerCase();
  const orgName = (proc.orgs?.find(o => o.id === party.org)?.name || "").toLowerCase();
  const combined = `${label} ${orgName}`;
  if (/legal|counsel|attorney/.test(combined)) return "legal";
  if (/procure|sourcing|purchasing|buyer.*ops/.test(combined)) return "procurement";
  if (/finance|accounts|ap |a\/p|payroll|cfo|controller/.test(combined)) return "finance";
  if (/hr |people |benefits|recruit|hiring|talent/.test(combined)) return "hr";
  if (/security|infosec|cyber|risk/.test(combined)) return "security";
  if (/engineer|technical|implement|develop|solution/.test(combined)) return "engineering";
  if (/sales|account exec|ae\b|csm|customer success/.test(combined)) return "sales";
  if (/gov|agency|usda|fda|dep|department|authority|municipal|state|inspector|regulator/.test(combined)) return "government";
  // Anything in an "external" org → external party
  if (/external|provider|partner|vendor|carrier|broker|notary|kyc|bureau|underwriter/.test(combined)) return "external";
  return "default";
}

function headcountFor(party: ProcessParty, type: PartyType, roster: RosterShape): number {
  // External parties stay small regardless of internal org size.
  if (type === "external" || type === "government") {
    return roster.external;
  }
  // Customer-side functions in a Vendor↔Customer process get the customer
  // size — but in a single-org process there's no "customer".
  const orgIsCustomer = /customer|buyer/.test(party.org || "");
  if (orgIsCustomer) return roster.customer;
  return roster.internal;
}

function lerp([lo, hi]: [number, number], u: number): number {
  return lo + (hi - lo) * u;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h;
}
